import * as React from 'react';
import * as ReactDOM from 'react-dom';

import styled from '@emotion/styled';
import { type WrappedFieldProps } from 'redux-form';

import Input from '~/components/Input';
import Label, { type LabelProps } from '~/components/Label/Label';
import SelectButton from '~/components/SelectButton';
import { InputSize, type HeightSize } from '~/constants/form';
import { Keys } from '~/constants/keys';
import { colors, fontWeight } from '~/styles/constants';

import Dropdown from '../Dropdown';

interface SelectListProps {
  width: number;
}

const SelectList = styled('ul')<SelectListProps>`
  padding: 0;
  margin: 0;
  max-height: 300px;
  overflow-y: auto;
`;

interface OptionProps {
  isActive: boolean;
  isHighlighted: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: (evt: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMouseEnter: (evt: any) => void;
}

const Option = styled('li')<OptionProps>`
  padding: 8px 0 8px 30px;
  font-size: 14px;
  line-height: 14px;
  color: ${colors.fontColor};
  ${(props) => (props.isActive ? `font-weight: ${fontWeight.medium};` : '')};
  ${(props) => (props.isHighlighted ? `background-color: ${colors.gray25};` : '')};
  position: relative;
  cursor: pointer;

  :first-of-type {
    margin-top: 8px;
  }

  :last-of-type {
    margin-bottom: 8px;
  }
`;

const InputContainer = styled('div')`
  margin: 3px;
`;

const SpinnerContainer = styled('div')`
  width: 100%;
  text-align: center;
  overflow: hidden;
`;

const OptionSelectedIcon = styled('span')`
  position: absolute;
  left: 12px;
  font-size: 10px;
  line-height: 15px;
`;

const ErrorMessage = styled('div')`
  font-size: 12px;
  color: ${colors.dangerRed};
  margin-top: 5px;
`;

export type Option = {
  component?: JSX.Element;
  label: JSX.Element | string;
  value: boolean | number | string;
};

interface ContainerProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyDown: (evt: any) => void;
  responsive: boolean;
}

const Container = styled('div')<ContainerProps>`
  display: ${({ responsive }) => (responsive ? 'block' : 'inline-block')};
`;

interface SmartSelectProps {
  dataE2E?: string;
  dataPendo?: string;
  disabled?: boolean;
  // Props to display a loading state
  displaySpinner?: boolean;
  errorMessage?: string;
  height?: HeightSize;
  hideErrors?: boolean;
  invalid?: boolean;
  isOpen?: boolean;
  label?: string;
  labelProps?: LabelProps;
  onChange?(option: Option): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyDown?: any;
  options?: Option[];
  placeholder?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderSelectButtonContent?: (selectedOption: Option) => any;
  responsive?: boolean;
  size?: InputSize;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  spinnerComponent?: any;
  tabIndex?: number;
  value?: Maybe<Option>;
}

interface SmartSelectState {
  availableOptions?: Option[];
  highlightedOptionIndex: number;
  inputValue: string;
  isOpen: boolean;
}

class SmartSelect extends React.Component<SmartSelectProps, SmartSelectState> {
  static defaultProps: Partial<SmartSelectProps> = {
    size: InputSize.medium,
    value: null,
  };
  onClick = () => {
    this.setState({
      isOpen: !this.state.isOpen,
    });
  };

  onClickOutside = (evt) => {
    // If the element clicked outside is inside trigger, let the trigger on click toggle
    if (evt.propagationPath().indexOf(ReactDOM.findDOMNode(this.trigger)) !== -1) return;

    this.setState({
      isOpen: false,
    });
  };

  onClose = () => {
    // Reset filtered list
    this.onInputChange('');

    this.setState({
      highlightedOptionIndex: 0,
      isOpen: false,
    });
  };

  onInputChange = (value) => {
    const { options } = this.props;
    if (!options) return null;

    let filteredOptions;
    if (!value) {
      filteredOptions = options;
    } else {
      const trimmedValue = value.trim().toLowerCase();
      const inputLength = trimmedValue.length;

      filteredOptions =
        inputLength === 0
          ? []
          : options
              .filter(({ label }) => (label as string).toLowerCase().includes(trimmedValue))
              .sort((a, b) => {
                if (
                  (a.label as string).toLowerCase().slice(0, inputLength) ===
                  (b.label as string).toLowerCase().slice(0, inputLength)
                )
                  return 0;

                if ((a.label as string).toLowerCase().slice(0, inputLength) === trimmedValue)
                  return -1;

                if ((b.label as string).toLowerCase().slice(0, inputLength) === trimmedValue)
                  return 1;

                if (a.label < b.label) return -1;
                if (a.label > b.label) return 1;
                return 0;
              })
              .map((option, idx) => this.highlightOption(trimmedValue, option, idx));
    }

    this.setState({
      availableOptions: filteredOptions,
      inputValue: value,
    });
  };

  onKeyDown = (evt: React.KeyboardEvent) => {
    switch (evt.key) {
      case Keys.ArrowDown:
        if (!this.state.isOpen) return this.onClick();
        evt.preventDefault();
        evt.stopPropagation();

        if (this.props.options) {
          const optionsLength = this.props.options.length;

          return this.setState((state) => ({
            ...state,
            highlightedOptionIndex:
              state.highlightedOptionIndex + 1 >= optionsLength - 1
                ? optionsLength - 1
                : state.highlightedOptionIndex + 1,
          }));
        }
        break;
      case Keys.ArrowUp:
        evt.preventDefault();
        evt.stopPropagation();
        return this.setState((state) => ({
          ...state,
          highlightedOptionIndex:
            state.highlightedOptionIndex - 1 < 0 ? 0 : state.highlightedOptionIndex - 1,
        }));
      case Keys.Enter:
        // Select highlighted option
        if (!this.state.isOpen) return this.onClick();
        evt.preventDefault();
        evt.stopPropagation();

        if (this.state.availableOptions) {
          const selectedOption = this.state.availableOptions[this.state.highlightedOptionIndex];
          // This means the user searched for something and there are no options available. So we return.
          if (!selectedOption) return;

          return this.selectOption(selectedOption);
        }
        break;
      case Keys.Escape:
      case Keys.Tab:
        // Close and reset
        return this.onClose();
      default:
        if (!this.state.isOpen) return this.onClick();
    }
  };

  renderDropdownOption = (option: Option, index: number) => {
    const selectedOption = this.props.value;
    let selectedIcon: Maybe<JSX.Element> = null;
    let optionIsActive = false;

    if (selectedOption && option.value === selectedOption.value) {
      selectedIcon = (
        <OptionSelectedIcon>
          <i className="fa fa-check" />
        </OptionSelectedIcon>
      );
      optionIsActive = true;
    }

    return (
      <Option
        isActive={optionIsActive}
        isHighlighted={this.state.highlightedOptionIndex === index}
        key={String(option.value)}
        onClick={this.selectOption.bind(this, option)}
        onMouseEnter={this.setHighlightedIndex(index)}
      >
        {selectedIcon}
        {option.component ? option.component : option.label}
      </Option>
    );
  };

  renderInput = () => (
    <InputContainer>
      <Input autofocus onChange={this.onInputChange} responsive>
        {this.state.inputValue}
      </Input>
    </InputContainer>
  );

  renderSelectList = () => {
    const { options } = this.props;
    const { availableOptions } = this.state;
    if (!options) return null;

    const Spinner = this.props.spinnerComponent || null;

    const spinner = this.props.displaySpinner ? (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    ) : null;

    return (
      <React.Fragment>
        {this.renderInput()}
        <SelectList width={this.props.size!}>
          {availableOptions?.map(this.renderDropdownOption)}
          {spinner}
        </SelectList>
      </React.Fragment>
    );
  };

  selectOption = (option: Option) => {
    const { onChange } = this.props;

    if (onChange) onChange(option);

    this.onClose();
  };

  setHighlightedIndex = (index: number) => () => {
    this.setState({ highlightedOptionIndex: index });
  };

  setTriggerRef = (node: React.ReactNode) => (this.trigger = node);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trigger: any = null;

  constructor(props: SmartSelectProps) {
    super(props);

    this.state = {
      availableOptions: props.options,
      highlightedOptionIndex: 0,
      inputValue: '',
      isOpen: false,
    };
  }

  componentDidUpdate(prevProps: SmartSelectProps) {
    if (prevProps.options !== this.props.options) {
      // Reset option list
      this.onInputChange('');
    }
  }

  highlightOption(searchString, option, idx) {
    const { label } = option;
    const foundTokenStartIdx = label.toLowerCase().indexOf(searchString);
    const first = label.slice(0, foundTokenStartIdx);
    const highlightedToken = label.slice(
      foundTokenStartIdx,
      foundTokenStartIdx + searchString.length,
    );
    const rest = label.slice(foundTokenStartIdx + searchString.length, label.length);
    return {
      ...option,
      component: (
        <React.Fragment key={idx + label}>
          {first}
          <strong>{highlightedToken}</strong>
          {rest}
        </React.Fragment>
      ),
    };
  }

  render() {
    const {
      dataE2E,
      dataPendo,
      disabled,
      height,
      invalid,
      label,
      placeholder,
      renderSelectButtonContent,
      responsive,
      size,
      tabIndex,
    } = this.props;
    const selectedOption = this.props.value;
    let buttonText = selectedOption ? selectedOption.label : placeholder;
    buttonText = renderSelectButtonContent
      ? renderSelectButtonContent(selectedOption!)
      : buttonText;

    const triggerComponent = () => (
      <SelectButton
        buttonRef={this.setTriggerRef}
        dataE2E={dataE2E}
        dataPendo={dataPendo}
        disabled={disabled}
        hasOptionSelected={!!selectedOption}
        height={height}
        invalid={invalid}
        onClick={this.onClick}
        responsive={responsive}
        size={size}
        tabIndex={tabIndex}
      >
        {buttonText}
      </SelectButton>
    );

    const labelComponent = label ? <Label {...this.props.labelProps}>{label}</Label> : null;

    return (
      <Container onKeyDown={this.onKeyDown} responsive={responsive!}>
        {labelComponent}
        <Dropdown
          isOpen={this.state.isOpen || this.props.isOpen}
          minWidth={size}
          onClickOutside={this.onClickOutside}
          onClose={this.onClose}
          triggerComponent={triggerComponent}
        >
          {this.renderSelectList()}
        </Dropdown>
        {this.renderErrorMessage()}
      </Container>
    );
  }

  renderErrorMessage() {
    const { errorMessage } = this.props;

    if (!errorMessage) return null;

    return <ErrorMessage>{errorMessage}</ErrorMessage>;
  }

  shouldComponentUpdate(nextState: SmartSelectState) {
    return nextState.availableOptions !== this.state.availableOptions;
  }
}

export const SmartSelectField = (props: SmartSelectProps & WrappedFieldProps) => {
  const { hideErrors, input, invalid, meta, ...selectProps } = props;

  const errorMessage = !hideErrors && meta && meta.touched && meta.error ? meta.error : null;

  return (
    <SmartSelect
      onChange={input.onChange}
      value={input.value}
      {...selectProps}
      errorMessage={errorMessage}
      invalid={invalid || errorMessage !== null}
    />
  );
};

export default SmartSelect;
